/**
 * AuthCallbackScreen — receives the magic-link confirmation from
 * Supabase Auth, exchanges the URL fragment for a session, and
 * routes the user into the app (caseload) on success.
 *
 * Why a dedicated screen: Supabase's emailRedirectTo flow puts the
 * `access_token` + `refresh_token` in the URL fragment when the
 * magic-link is confirmed in a browser. The token exchange has to
 * happen client-side (the tokens are in our window.location.hash,
 * not accessible to the server). After exchange we route to
 * /caseload and drop the URL fragment.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';

interface Props {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export function AuthCallbackScreen({ onSuccess, onError }: Props): ReactElement {
  const [status, setStatus] = useState<'exchanging' | 'success' | 'error'>('exchanging');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    void (async () => {
      // Supabase v2 supports both fragment (implicit) and PKCE.
      // We use the implicit flow because it ships without a backend
      // round-trip and matches the web SPA pattern.
      const hash = window.location.hash.replace(/^#/, '');
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const errorDescription = params.get('error_description');

      if (errorDescription) {
        setStatus('error');
        setMessage(errorDescription);
        onError?.(errorDescription);
        return;
      }

      if (!accessToken || !refreshToken) {
        setStatus('error');
        setMessage(
          'No session token in the URL. The magic link may have expired (1 hour) or been used already.',
        );
        onError?.('no_token');
        return;
      }

      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        setStatus('error');
        setMessage(error.message);
        onError?.(error.message);
        return;
      }

      // Drop the fragment so the tokens aren't bookmarked or
      // accidentally leaked via the URL bar.
      window.history.replaceState({}, '', window.location.pathname);
      setStatus('success');
      onSuccess?.();
    })();
  }, [onSuccess, onError]);

  if (status === 'exchanging') {
    return (
      <main className="screen auth">
        <header className="screen-header">
          <h1>Signing you in…</h1>
          <p className="tagline">Verifying your magic link.</p>
        </header>
        <p className="tagline">This usually takes a second.</p>
      </main>
    );
  }

  if (status === 'error') {
    return (
      <main className="screen auth">
        <header className="screen-header">
          <h1>Sign-in failed</h1>
          <p className="tagline">{message}</p>
        </header>
        <p className="tagline">
          Try requesting a new magic link from the sign-in page.
        </p>
      </main>
    );
  }

  return (
    <main className="screen auth">
      <header className="screen-header">
        <h1>Signed in</h1>
        <p className="tagline">Redirecting to your caseload…</p>
      </header>
    </main>
  );
}